import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, Plus, Filter, ChevronRight, Building2,
  CheckCircle2, Clock, AlertCircle, XCircle, FileText,
  RefreshCw, Users, TrendingUp, Settings2, ChevronDown, ChevronUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_CONFIG = {
  borrador:         { label: "Borrador",         color: "bg-slate-100 text-slate-700 border-slate-200" },
  calculado:        { label: "Calculado",        color: "bg-blue-100 text-blue-800 border-blue-200" },
  autorizado:       { label: "Autorizado",       color: "bg-amber-100 text-amber-800 border-amber-200" },
  pagado:           { label: "Pagado",           color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  timbrado:         { label: "Timbrado",         color: "bg-green-100 text-green-800 border-green-200" },
  timbrado_parcial: { label: "Timbrado parcial", color: "bg-orange-100 text-orange-800 border-orange-200" },
  cerrado:          { label: "Cerrado",          color: "bg-gray-100 text-gray-600 border-gray-200" },
};

const PERIOD_TYPE_LABELS = { semanal: "Semanal", quincenal: "Quincenal", mensual: "Mensual" };

export default function OpsRRHHNomina() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [periods, setPeriods] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [legalEntities, setLegalEntities] = useState([]);
  const [filterEntity, setFilterEntity] = useState("");
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 30;

  const isHR = ["hr_admin", "corporate_admin"].includes(user?.role);

  const [showKpiConfig, setShowKpiConfig] = useState(false);
  const [kpiMapping, setKpiMapping] = useState({});
  const [kpiPositions, setKpiPositions] = useState([]);
  const [kpiCategories, setKpiCategories] = useState([]);
  const [savingMapping, setSavingMapping] = useState(false);

  const loadKpiMapping = useCallback(async () => {
    try {
      const r = await api.get("/ops/payroll/kpi-category-mapping");
      setKpiMapping(r.data.mapping || {});
      setKpiPositions(r.data.positions || []);
      setKpiCategories(r.data.categories || []);
    } catch {
      toast.error("Error al cargar el mapeo de KPI");
    }
  }, []);

  useEffect(() => { if (isHR && showKpiConfig) loadKpiMapping(); }, [isHR, showKpiConfig, loadKpiMapping]);

  const handleSaveMapping = async () => {
    setSavingMapping(true);
    try {
      await api.put("/ops/payroll/kpi-category-mapping", { mapping: kpiMapping });
      toast.success("Mapeo guardado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingMapping(false);
    }
  };

  useEffect(() => {
    api.get("/ops/legal-entities").then(r => setLegalEntities(Array.isArray(r.data) ? r.data : r.data?.entities || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filterEntity) params.set("legal_entity_id", filterEntity);
      if (filterYear) params.set("year", filterYear);
      if (filterStatus) params.set("status", filterStatus);
      const r = await api.get(`/ops/payroll/periods?${params}`);
      setPeriods(r.data.periods || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error("Error al cargar periodos de nómina");
    } finally {
      setLoading(false);
    }
  }, [page, filterEntity, filterYear, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const fmt = n => n?.toLocaleString("es-MX", { style: "currency", currency: "MXN" }) ?? "$0.00";

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-[#FFDF00]" />
            Nomina
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Periodos de pago y calculo de nomina mexicana</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={load} data-testid="nomina-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isHR && (
            <Button
              className="bg-[#FFDF00] text-slate-900 hover:bg-yellow-400 font-semibold"
              onClick={() => navigate("/ops/rrhh/nomina/nuevo")}
              data-testid="nomina-nuevo-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuevo periodo
            </Button>
          )}
        </div>
      </div>

      {/* Configuración: mapeo puesto -> categoría de KPI (bono de nómina) */}
      {isHR && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            onClick={() => setShowKpiConfig(v => !v)}
            data-testid="toggle-kpi-mapping-config"
          >
            <span className="font-semibold text-sm text-slate-700 flex items-center gap-1.5">
              <Settings2 className="w-4 h-4" /> Configurar bono KPI por puesto
            </span>
            {showKpiConfig ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showKpiConfig && (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-500">
                El bono KPI de la nómina se calcula según la categoría de KPI asignada a cada puesto.
                Si un puesto no tiene categoría, el bono sale en $0 con una nota explicativa.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kpi-mapping-grid">
                {kpiPositions.map(p => (
                  <div key={p.code} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-slate-700">{p.label || p.code}</span>
                    <select
                      className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
                      value={kpiMapping[p.code] || ""}
                      onChange={e => setKpiMapping(m => ({ ...m, [p.code]: e.target.value }))}
                      data-testid={`kpi-mapping-select-${p.code}`}
                    >
                      <option value="">Sin categoría</option>
                      {kpiCategories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <Button size="sm" onClick={handleSaveMapping} disabled={savingMapping}
                className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-kpi-mapping-btn">
                {savingMapping ? "Guardando..." : "Guardar mapeo"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 min-w-[200px]"
          value={filterEntity}
          onChange={e => { setFilterEntity(e.target.value); setPage(1); }}
          data-testid="filter-entity"
        >
          <option value="">Todas las razones sociales</option>
          {legalEntities.map(e => (
            <option key={e.id} value={e.id}>{e.legal_name}</option>
          ))}
        </select>
        <Input
          type="number"
          placeholder="Año"
          className="w-24 text-sm"
          value={filterYear}
          onChange={e => { setFilterYear(e.target.value); setPage(1); }}
          data-testid="filter-year"
        />
        <select
          className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          data-testid="filter-status"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-slate-400">Cargando periodos...</div>
      ) : periods.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="nomina-empty-state">
          <DollarSign className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Sin periodos de nomina</p>
          {isHR && (
            <Button
              className="mt-4 bg-[#FFDF00] text-slate-900 hover:bg-yellow-400 font-semibold"
              onClick={() => navigate("/ops/rrhh/nomina/nuevo")}
            >
              <Plus className="w-4 h-4 mr-1" /> Crear primer periodo
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid="nomina-periods-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Periodo</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Razon social</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Tipo</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Fechas</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Empleados</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Total bruto</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Neto</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Estado</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Fecha pago</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, idx) => {
                const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.borrador;
                return (
                  <tr
                    key={p.id}
                    className={`border-b cursor-pointer hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? "" : "bg-slate-50/40"}`}
                    onClick={() => navigate(`/ops/rrhh/nomina/${p.id}`)}
                    data-testid={`period-row-${p.id}`}
                  >
                    <td className="px-4 py-3 font-mono font-medium text-slate-800">{p.period_number}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate">{p.legal_entity_name || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{PERIOD_TYPE_LABELS[p.period_type] || p.period_type}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{p.start_date} — {p.end_date}</td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      {p.totals?.employees_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{fmt(p.totals?.gross_total)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(p.totals?.net_total)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${sc.color}`}>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{p.payment_date}</td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-between items-center text-sm text-slate-500">
          <span>{total} periodos en total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              Anterior
            </Button>
            <span className="px-3 py-1 text-xs">Pag {page}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * limit >= total}>
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
